import React from 'react';
import Survey, {
  FieldValueUpdate,
  RepeatingGroupInstanceRef,
  SurveyStatus,
} from '../util/Survey';
import SurveyResponse from '../util/SurveyResponse';
import noop from 'lodash/noop';
import { ConfigOptions } from './SurveyBuilder/useBuilder';

// @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
const SurveyContext = React.createContext<Survey>(null);

export type onSurveyUpdateSignature = (
  survey: Survey,
  bypassStatusCheck?: boolean,
) => void;

// @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
const SurveyUpdateContext = React.createContext<onSurveyUpdateSignature>(null);

type onFieldUpdateSignature = (updates: FieldValueUpdate[]) => void;

// @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
const FieldUpdateContext = React.createContext<onFieldUpdateSignature>(null);

// @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
const SurveyResponseContext = React.createContext<SurveyResponse>(null);

const IsEnabledContext = React.createContext<boolean>(false);

const IsReadOnlyContext = React.createContext<boolean>(true);

const AutoSaveContext = React.createContext<boolean>(false);

/**
 * Set while rendering the sub-items of one repeating group instance.
 * namePrefix qualifies formik field names (e.g. "sisters[1]." or, nested,
 * "sisters[1].daughters[0]."), groupLink / instanceIndex scope enableWhen
 * resolution to the innermost instance.  ancestors carries the full instance
 * chain (outermost first, including the innermost) so nested repeating
 * groups resolve every level.
 */
export type RepeatingGroupInstance = {
  groupLink: string;
  instanceIndex: number;
  namePrefix: string;
  ancestors: RepeatingGroupInstanceRef[];
};

export const RepeatingGroupInstanceContext =
  React.createContext<RepeatingGroupInstance | null>(null);

export type RepeatingGroupActions = {
  /**
   * displayedInstanceCount is the number of instance cards currently
   * rendered, which may exceed the answered count when the renderer pads to
   * minOccurs.  The handler materializes the padded instances before
   * appending so the add is immediately visible.
   */
  onAddInstance: (groupPath: string[], displayedInstanceCount?: number) => void;
  onRemoveInstance: (groupPath: string[], instanceIndex: number) => void;
};

export const RepeatingGroupActionsContext =
  React.createContext<RepeatingGroupActions>({
    onAddInstance: noop,
    onRemoveInstance: noop,
  });

// @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
const SurveyBuilderActivePageContext = React.createContext<string>(null);

const ProjectQuestionnairesContext = React.createContext<fhir.Questionnaire[]>(
  [],
);

// @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
const SubjectIdContext = React.createContext<string>(null);

export type RegisterSurveyItemContainerRef = (
  linkId: string,
  ref: HTMLDivElement,
) => void;

export const RegisterSurveyItemContainerRefContext =
  React.createContext<RegisterSurveyItemContainerRef>(noop);

export type TriggerScrollToSurveyItem = (linkId: string) => void;

export const TriggerScrollToSurveyItemContext =
  React.createContext<TriggerScrollToSurveyItem>(noop);

export type RegisterCollapsibleSection = (
  linkId: string,
  expand: (() => void) | null,
) => void;

export const RegisterCollapsibleSectionContext =
  React.createContext<RegisterCollapsibleSection>(noop);

export type onUpdateSurveyBuilderActivePageSignature = (
  activePage: string,
) => void;

const UpdateSurveyBuilderActivePageContext =
  // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
  React.createContext<onUpdateSurveyBuilderActivePageSignature>(null);

// @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
const EditFocusContext = React.createContext<string>(null);

export type ListEditState = {
  isListEditOpen: boolean;
  createOnlyMode: boolean;
};

export const defaultListEditState: ListEditState = {
  isListEditOpen: false,
  createOnlyMode: false,
};

export const ListEditStateContext =
  React.createContext<ListEditState>(defaultListEditState);

export type OnUpdateListEditState = (newListEditState: ListEditState) => void;

export const UpdateListEditStateContext =
  React.createContext<OnUpdateListEditState>(noop);

export type ScoreLabelsEditState = {
  isScoreLabelsEditOpen: boolean;
};

export const defaultScoreLabelsEditState: ScoreLabelsEditState = {
  isScoreLabelsEditOpen: false,
};

export const ScoreLabelsEditStateContext =
  React.createContext<ScoreLabelsEditState>(defaultScoreLabelsEditState);

export type OnUpdateScoreLabelsEditState = (
  newScoreLabelsEditState: ScoreLabelsEditState,
) => void;

export const UpdateScoreLabelsEditStateContext =
  React.createContext<OnUpdateScoreLabelsEditState>(noop);

export type OnSurveyPreview = (launchFromPageLink?: string) => void;

export const OnSurveyPreviewContext =
  React.createContext<OnSurveyPreview>(noop);

export type OnSetSurveyStatus = (surveyStatus: SurveyStatus) => void;

export const OnSetSurveyStatusContext =
  React.createContext<OnSetSurveyStatus>(noop);

export interface UpdateEditFocus {
  onSetEditFocus: (focusedItem: string) => void;
  onClearEditFocus: () => void;
}

export type CollapsedHash = { [itemLinkId: string]: boolean };
export type OnToggleCollapse = (itemLinkId: string) => void;

export const CollapsedHashContext = React.createContext<CollapsedHash>({});
export const ToggleCollapseContext =
  React.createContext<OnToggleCollapse>(noop);

const defaultUpdateEditFocus = {
  onSetEditFocus: noop,
  onClearEditFocus: noop,
} as UpdateEditFocus;

const UpdateEditFocusContext = React.createContext<UpdateEditFocus>(
  defaultUpdateEditFocus,
);

export type WithAbacContextProps = {
  userCanEdit: boolean;
};

const AbacContext = React.createContext<WithAbacContextProps>({
  userCanEdit: true,
});

export type WithConfigOptionsContextProps = {
  configOptions: ConfigOptions;
};

const defaultConfigOptions = {
  surveyShowScoreEnabled: false,
  variantSelectionEnabled: false,
};

const ConfigOptionsContext =
  React.createContext<ConfigOptions>(defaultConfigOptions);

export interface WithSurveyContextInjectedProps {
  survey?: Survey;
}

const withSurveyContext =
  <P extends {}>(
    WrappedComponent: React.ComponentType<P & WithSurveyContextInjectedProps>,
  ) =>
  (props: P) =>
    (
      <SurveyContext.Consumer>
        {(survey: Survey) => <WrappedComponent {...props} survey={survey} />}
      </SurveyContext.Consumer>
    );

export interface WithProjectQuestionnairesInjectedProps {
  projectQuestionnaires: fhir.Questionnaire[];
}

const withProjectQuestionnaires =
  <P extends {}>(
    WrappedComponent: React.ComponentType<
      P & WithProjectQuestionnairesInjectedProps
    >,
  ) =>
  (props: P) =>
    (
      <ProjectQuestionnairesContext.Consumer>
        {(projectQuestionnaires: fhir.Questionnaire[]) => (
          <WrappedComponent
            {...props}
            projectQuestionnaires={projectQuestionnaires}
          />
        )}
      </ProjectQuestionnairesContext.Consumer>
    );

export interface WithSurveyStatusInjectedProps {
  surveyStatus?: SurveyStatus;
}

const withSurveyStatus =
  <P extends {}>(
    WrappedComponent: React.ComponentType<P & WithSurveyStatusInjectedProps>,
  ) =>
  (props: P) =>
    (
      <SurveyContext.Consumer>
        {(survey: Survey) => (
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          <WrappedComponent
            {...props}
            surveyStatus={survey ? survey.status : null}
          />
        )}
      </SurveyContext.Consumer>
    );

export interface WithSurveyUpdateInjectedProps {
  onSurveyUpdate: onSurveyUpdateSignature;
}

const withOnSurveyUpdateContext =
  <P extends {}>(
    WrappedComponent: React.ComponentType<P & WithSurveyUpdateInjectedProps>,
  ) =>
  (props: P) =>
    (
      <SurveyUpdateContext.Consumer>
        {(onSurveyUpdate: onSurveyUpdateSignature) => (
          <WrappedComponent {...props} onSurveyUpdate={onSurveyUpdate} />
        )}
      </SurveyUpdateContext.Consumer>
    );

export interface WithOnFieldUpdateInjectedProps {
  onFieldUpdate: onFieldUpdateSignature;
}

const withOnFieldUpdateContext =
  <P extends {}>(
    WrappedComponent: React.ComponentType<P & WithOnFieldUpdateInjectedProps>,
  ) =>
  (props: P) =>
    (
      <FieldUpdateContext.Consumer>
        {(onFieldUpdate: onFieldUpdateSignature) => (
          <WrappedComponent {...props} onFieldUpdate={onFieldUpdate} />
        )}
      </FieldUpdateContext.Consumer>
    );

export interface WithSurveyResponseContextInjectedProps {
  surveyResponse: SurveyResponse;
}

const withSurveyResponseContext =
  <P extends {}>(
    WrappedComponent: React.ComponentType<
      P & WithSurveyResponseContextInjectedProps
    >,
  ) =>
  (props: P) =>
    (
      <SurveyResponseContext.Consumer>
        {(surveyResponse: SurveyResponse) => (
          <WrappedComponent {...props} surveyResponse={surveyResponse} />
        )}
      </SurveyResponseContext.Consumer>
    );

export interface WithSurveyResponseIdInjectedProps {
  surveyResponseId: string;
}

const withSurveyResponseId =
  <P extends {}>(
    WrappedComponent: React.ComponentType<
      P & WithSurveyResponseIdInjectedProps
    >,
  ) =>
  (props: P) =>
    (
      <SurveyResponseContext.Consumer>
        {(surveyResponse: SurveyResponse) => (
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          <WrappedComponent {...props} surveyResponseId={surveyResponse?.id} />
        )}
      </SurveyResponseContext.Consumer>
    );

export interface WithIsEnabledInjectedProps {
  isEnabled: boolean;
}

const withIsEnabledContext =
  <P extends {}>(
    WrappedComponent: React.ComponentType<P & WithIsEnabledInjectedProps>,
  ) =>
  (props: P) =>
    (
      <IsEnabledContext.Consumer>
        {(isEnabled: boolean) => (
          <WrappedComponent {...props} isEnabled={isEnabled} />
        )}
      </IsEnabledContext.Consumer>
    );

export interface WithRepeatingGroupInstanceInjectedProps {
  repeatingGroupInstance: RepeatingGroupInstance | null;
}

const withRepeatingGroupInstance =
  <P extends {}>(
    WrappedComponent: React.ComponentType<
      P & WithRepeatingGroupInstanceInjectedProps
    >,
  ) =>
  (props: P) =>
    (
      <RepeatingGroupInstanceContext.Consumer>
        {(repeatingGroupInstance: RepeatingGroupInstance | null) => (
          <WrappedComponent
            {...props}
            repeatingGroupInstance={repeatingGroupInstance}
          />
        )}
      </RepeatingGroupInstanceContext.Consumer>
    );

export interface WithIsReadOnlyInjectedProps {
  isReadOnly: boolean;
}

const withIsReadOnly =
  <P extends {}>(
    WrappedComponent: React.ComponentType<P & WithIsReadOnlyInjectedProps>,
  ) =>
  (props: P) =>
    (
      <IsReadOnlyContext.Consumer>
        {(isReadOnly: boolean) => (
          <WrappedComponent {...props} isReadOnly={isReadOnly} />
        )}
      </IsReadOnlyContext.Consumer>
    );

export interface WithSubjectIdInjectedProps {
  subjectId?: string;
}

const withSubjectId =
  <P extends {}>(
    WrappedComponent: React.ComponentType<P & WithSubjectIdInjectedProps>,
  ) =>
  (props: P) =>
    (
      <SubjectIdContext.Consumer>
        {(subjectId: string) => (
          <WrappedComponent {...props} subjectId={subjectId} />
        )}
      </SubjectIdContext.Consumer>
    );

export interface SurveyBuilderActivePageInjectedProps {
  surveyBuilderActivePage: string;
}

const withSurveyBuilderActivePage =
  <P extends {}>(
    WrappedComponent: React.ComponentType<
      P & SurveyBuilderActivePageInjectedProps
    >,
  ) =>
  (props: P) =>
    (
      <SurveyBuilderActivePageContext.Consumer>
        {(surveyBuilderActivePage: string) => (
          <WrappedComponent
            {...props}
            surveyBuilderActivePage={surveyBuilderActivePage}
          />
        )}
      </SurveyBuilderActivePageContext.Consumer>
    );

export interface WithOnUpdateSurveyBuilderActivePageInjectedProps {
  onUpdateSurveyBuilderActivePage: onUpdateSurveyBuilderActivePageSignature;
}

const withOnUpdateSurveyBuilderActivePage =
  <P extends {}>(
    WrappedComponent: React.ComponentType<
      P & WithOnUpdateSurveyBuilderActivePageInjectedProps
    >,
  ) =>
  (props: P) =>
    (
      <UpdateSurveyBuilderActivePageContext.Consumer>
        {(
          onUpdateSurveyBuilderActivePage: onUpdateSurveyBuilderActivePageSignature,
        ) => (
          <WrappedComponent
            {...props}
            onUpdateSurveyBuilderActivePage={onUpdateSurveyBuilderActivePage}
          />
        )}
      </UpdateSurveyBuilderActivePageContext.Consumer>
    );

export interface WithEditFocusInjectedProps {
  editFocus: string;
}

const withEditFocus =
  <P extends {}>(
    WrappedComponent: React.ComponentType<P & WithEditFocusInjectedProps>,
  ) =>
  (props: P) =>
    (
      <EditFocusContext.Consumer>
        {(editFocus: string) => (
          <WrappedComponent {...props} editFocus={editFocus} />
        )}
      </EditFocusContext.Consumer>
    );

export interface WithIsEditFocusedInjectedProps {
  isEditFocused?: boolean;
}

/**
 * A derivation of withEditFocus.  Injects a boolean if ANY edit is focused.  Ensures less re-renders as this will change less than the focused edit.
 */
const withIsEditFocused =
  <P extends {}>(
    WrappedComponent: React.ComponentType<P & WithIsEditFocusedInjectedProps>,
  ) =>
  (props: P) =>
    (
      <EditFocusContext.Consumer>
        {(editFocus: string) => (
          <WrappedComponent {...props} isEditFocused={editFocus !== null} />
        )}
      </EditFocusContext.Consumer>
    );

export interface WithUpdateEditFocusInjectedProps {
  onSetEditFocus: (focusedItem: string) => void;
  onClearEditFocus: () => void;
}

const withUpdateEditFocus =
  <P extends {}>(
    WrappedComponent: React.ComponentType<P & WithUpdateEditFocusInjectedProps>,
  ) =>
  (props: P) =>
    (
      <UpdateEditFocusContext.Consumer>
        {(updateEditFocus: UpdateEditFocus) => (
          <WrappedComponent
            {...props}
            onSetEditFocus={updateEditFocus.onSetEditFocus}
            onClearEditFocus={updateEditFocus.onClearEditFocus}
          />
        )}
      </UpdateEditFocusContext.Consumer>
    );

export interface WithListEditInjectedProps {
  listEdit: ListEditState;
}

const withListEdit =
  <P extends {}>(
    WrappedComponent: React.ComponentType<P & WithListEditInjectedProps>,
  ) =>
  (props: P) =>
    (
      <ListEditStateContext.Consumer>
        {(listEdit: ListEditState) => (
          <WrappedComponent {...props} listEdit={listEdit} />
        )}
      </ListEditStateContext.Consumer>
    );

export interface WithOnUpdateListEditInjectedProps {
  onUpdateListEdit: OnUpdateListEditState;
}

const withOnUpdateListEdit =
  <P extends {}>(
    WrappedComponent: React.ComponentType<
      P & WithOnUpdateListEditInjectedProps
    >,
  ) =>
  (props: P) =>
    (
      <UpdateListEditStateContext.Consumer>
        {(onUpdateListEdit: OnUpdateListEditState) => (
          <WrappedComponent {...props} onUpdateListEdit={onUpdateListEdit} />
        )}
      </UpdateListEditStateContext.Consumer>
    );

export interface WithScoreLabelsInjectedProps {
  scoreLabelsEdit: ScoreLabelsEditState;
}

const withScoreLabelsEdit =
  <P extends {}>(
    WrappedComponent: React.ComponentType<P & WithScoreLabelsInjectedProps>,
  ) =>
  (props: P) =>
    (
      <ScoreLabelsEditStateContext.Consumer>
        {(scoreLabelsEdit: ScoreLabelsEditState) => (
          <WrappedComponent {...props} scoreLabelsEdit={scoreLabelsEdit} />
        )}
      </ScoreLabelsEditStateContext.Consumer>
    );

export interface WithOnUpdateScoreLabelsEditInjectedProps {
  onUpdateScoreLabelsEdit: OnUpdateScoreLabelsEditState;
}

const withOnUpdateScoreLabelsEdit =
  <P extends {}>(
    WrappedComponent: React.ComponentType<
      P & WithOnUpdateScoreLabelsEditInjectedProps
    >,
  ) =>
  (props: P) =>
    (
      <UpdateScoreLabelsEditStateContext.Consumer>
        {(onUpdateScoreLabelsEdit: OnUpdateScoreLabelsEditState) => (
          <WrappedComponent
            {...props}
            onUpdateScoreLabelsEdit={onUpdateScoreLabelsEdit}
          />
        )}
      </UpdateScoreLabelsEditStateContext.Consumer>
    );
export interface WithOnSurveyPreviewInjectedProps {
  onSurveyPreview: (launchFromPageLink?: string) => void;
}

const withOnSurveyPreview =
  <P extends {}>(
    WrappedComponent: React.ComponentType<P & WithOnSurveyPreviewInjectedProps>,
  ) =>
  (props: P) =>
    (
      <OnSurveyPreviewContext.Consumer>
        {(onSurveyPreview: (launchFromPageLink: string) => void) => (
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          <WrappedComponent {...props} onSurveyPreview={onSurveyPreview} />
        )}
      </OnSurveyPreviewContext.Consumer>
    );

export interface WithOnSetSurveyStatusInjectedProps {
  onSetSurveyStatus: (status: SurveyStatus) => void;
}

const withOnSetSurveyStatus =
  <P extends {}>(
    WrappedComponent: React.ComponentType<
      P & WithOnSetSurveyStatusInjectedProps
    >,
  ) =>
  (props: P) =>
    (
      <OnSetSurveyStatusContext.Consumer>
        {(onSetSurveyStatus: (status: SurveyStatus) => void) => (
          <WrappedComponent {...props} onSetSurveyStatus={onSetSurveyStatus} />
        )}
      </OnSetSurveyStatusContext.Consumer>
    );

export interface WithCollapseStateInjectedProps {
  collapsedHash: CollapsedHash;
  onToggleCollapse: OnToggleCollapse;
}

const withCollapsedState =
  <P extends {}>(
    WrappedComponent: React.ComponentType<P & WithCollapseStateInjectedProps>,
  ) =>
  (props: P) =>
    (
      <ToggleCollapseContext.Consumer>
        {(onToggleCollapse: OnToggleCollapse) => (
          <CollapsedHashContext.Consumer>
            {(collapsedHash: CollapsedHash) => (
              <WrappedComponent
                {...props}
                collapsedHash={collapsedHash}
                onToggleCollapse={onToggleCollapse}
              />
            )}
          </CollapsedHashContext.Consumer>
        )}
      </ToggleCollapseContext.Consumer>
    );

const withAbacContext =
  <P extends {}>(
    WrappedComponent: React.ComponentType<P & WithAbacContextProps>,
  ) =>
  (props: P) =>
    (
      <AbacContext.Consumer>
        {(abacValue: WithAbacContextProps) => (
          <WrappedComponent {...props} {...abacValue} />
        )}
      </AbacContext.Consumer>
    );

const withConfigOptionsContext =
  <P extends {}>(
    WrappedComponent: React.ComponentType<P & WithConfigOptionsContextProps>,
  ) =>
  (props: P) =>
    (
      <ConfigOptionsContext.Consumer>
        {(configOptions: ConfigOptions) => (
          <WrappedComponent {...props} configOptions={configOptions} />
        )}
      </ConfigOptionsContext.Consumer>
    );

export {
  SurveyContext,
  SurveyUpdateContext,
  FieldUpdateContext,
  SurveyResponseContext,
  IsEnabledContext,
  IsReadOnlyContext,
  AutoSaveContext,
  SubjectIdContext,
  ProjectQuestionnairesContext,
  withSurveyContext,
  withOnFieldUpdateContext,
  withSurveyResponseContext,
  SurveyBuilderActivePageContext,
  UpdateSurveyBuilderActivePageContext,
  EditFocusContext,
  UpdateEditFocusContext,
  AbacContext,
  ConfigOptionsContext,
  withIsEnabledContext,
  withIsReadOnly,
  withRepeatingGroupInstance,
  withSubjectId,
  withSurveyBuilderActivePage,
  withOnSurveyUpdateContext,
  withOnUpdateSurveyBuilderActivePage,
  withEditFocus,
  withIsEditFocused,
  withUpdateEditFocus,
  withListEdit,
  withOnUpdateListEdit,
  withScoreLabelsEdit,
  withOnUpdateScoreLabelsEdit,
  withOnSurveyPreview,
  withOnSetSurveyStatus,
  withSurveyStatus,
  withCollapsedState,
  withSurveyResponseId,
  withProjectQuestionnaires,
  withAbacContext,
  withConfigOptionsContext,
};
